import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Package, Truck, CheckCircle, Clock, X, ChevronRight } from "lucide-react";
import { useStore } from "@/src/contexts/StoreContext";
import { cn } from "@/src/lib/utils";

interface OrderHistoryProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OrderHistory({ isOpen, onClose }: OrderHistoryProps) {
  const { orders, formatPrice, fetchOrders } = useStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchOrders().then(() => setIsLoading(false));
    }
  }, [isOpen, fetchOrders]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "shipped":
        return <Truck className="w-5 h-5 text-blue-500" />;
      case "processing":
        return <Package className="w-5 h-5 text-yellow-500" />;
      case "paid":
        return <Clock className="w-5 h-5 text-purple-500" />;
      default:
        return <Clock className="w-5 h-5 text-white/40" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "text-green-400 bg-green-500/10 border-green-500/20";
      case "shipped":
        return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      case "processing":
        return "text-yellow-400 bg-yellow-500/10 border-yellow-500/20";
      case "paid":
        return "text-purple-400 bg-purple-500/10 border-purple-500/20";
      default:
        return "text-white/40 bg-white/5 border-white/10";
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-2xl max-h-[80vh] bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-white/60" />
            <h2 className="text-xl font-bold text-white">Your Orders</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Orders List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white/40">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <p className="text-white/40">No orders yet</p>
              <p className="text-sm text-white/30 mt-2">
                Your print orders will appear here
              </p>
            </div>
          ) : (
            orders.map((order) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden"
              >
                {/* Order Header */}
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(order.status)}
                    <div>
                      <p className="font-mono text-sm text-white/60">{order.order_number}</p>
                      <p className="text-xs text-white/40">
                        {new Date(order.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium capitalize border",
                      getStatusColor(order.status)
                    )}
                  >
                    {order.status}
                  </span>
                </div>

                {/* Order Items */}
                <div className="p-4 space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/50 flex-shrink-0">
                        <img
                          src={item.image_url}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">
                          {item.product_name}
                        </h4>
                        <p className="text-xs text-white/50">{item.variant_name}</p>
                        <p className="text-xs text-white/40">Qty: {item.quantity}</p>
                      </div>
                      <span className="text-sm font-medium text-white">
                        {formatPrice(item.unit_price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Order Footer */}
                <div className="p-4 bg-white/[0.01] border-t border-white/5 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-white/40">Total: </span>
                    <span className="font-semibold text-white">{formatPrice(order.total_amount)}</span>
                  </div>
                  {order.tracking_number && (
                    <button className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                      Track Order
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}